import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';

import { VersionService } from '../../services/version/version.service';
import { AuthService } from '../../services/auth/auth.service';
import { LoginService } from '../../services/login/login.service';

import { SliderData } from '../../models/slider.model';

import { CustomSliderComponent } from '../../shared/custom-slider/custom-slider.component';
import { CustomInputComponent } from '../../shared/custom-input/custom-input.component';
import { CustomButtonComponent } from '../../shared/custom-button/custom-button.component';

import { UpdatePasswordComponent } from './update-password/update-password.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { SelectWarehouseComponent } from './select-warehouse/select-warehouse.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { SnackbarService } from '../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CustomSliderComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CustomButtonComponent,
    MatButtonModule,
    MatCheckboxModule,
    RouterLink,
    ForgotPasswordComponent,
    UpdatePasswordComponent,
    SelectWarehouseComponent
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class LoginComponent implements OnInit {
  ELEMENT_IDS: { [key: string]: string } = ELEMENT_IDS;
  mode: 'login' | 'select-warehouse' | 'forgot-password' | 'change-password' = 'login';
  loginForm: FormGroup;
  errorMessage: string | null = null;
  loading: boolean = false;
  appVersion!: string;

  sliderData = SliderData;

  readonly dialog = inject(MatDialog);

  fpInitData: any = null;

  showPassword: boolean = false;

  warehouseList = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private loginService: LoginService,
    private versionService: VersionService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.appVersion = this.versionService.getApplicationVersion();
    this.subscribeToChanges();
  }

  subscribeToChanges() {
    // route changes
    this.route.queryParams.subscribe((param) => {
      this.mode = param['mode'];

      if (!this.mode) {
        this.mode = 'login';
        this.fpInitData = null;
        return;
      }

      this.fpInitData = param;
    });
  }

  onSubmit(): void {
    if (!this.loginForm.valid) {
      return;
    }
    if(this.loading) {
      return;
    }

    let username = this.loginForm.value.username;
    let password = this.loginForm.value.password;

    let authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    let cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.authService.userPool,
    });

    // this.key = _.get(value, 'username') + (+new Date).toString(36);

    let self = this;
    self.loading = true;
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async function (session) {
        sessionStorage.setItem('sessionData', 'loggedIn');
        await self.authService.initAWSCredentials().then(
          () => {
            console.log('AWSCredentials initialized');
          },
          (err) => {
            console.log('Failed to initialize AWSCredentials', err);
            self.errorMessage = "Authentication error";
          }
        );
        self.onAuthenticate();
      },
      onFailure: function (err) {
        err = String(err).split(":")[1];
        self.errorMessage = err;
        self.loading = false;
      },
      newPasswordRequired: function (userAttributes, requiredAttributes) {
        self.onUpdatePassword();

        self.loading = false;
      },
    });
  }

  onUpdatePassword() {
    this.router.navigate(['login'], {
      queryParams: {
        mode: 'change-password',
      },
    });
  }

  onForgotPassword() {
    const username = this.loginForm.get('username')?.value;

    this.router.navigate(['/login'], {
      queryParams: { mode: 'forgot-password', username: username },
    });
  }

  onAuthenticate() {
    this.getUserWarehouse();
  }

  private getUserWarehouse(): void {
    this.loginService.getWarehouse().subscribe({
        next: (res) => {
          if (res.data.length > 1) {
            this.warehouseList = res.data;
            this.mode = 'select-warehouse';
            
          }
          else if (res.data.length == 1) {
            this.onWarehouseSelect(res.data[0])
            // this.getUserDetail(res.data[0].database_schema);
          }
          this.loginService.availableWarehouse = res.data;
        },
        error: () => {
          this.errorMessage = 'Authentication error.';
          this.loading = false;
        }
    });
  }

  private getUserDetail(warehouse_db_schema: string): void {
    let parameters = {
        "warehouse_db_schema": warehouse_db_schema
    };
    this.loginService.login(parameters).subscribe({
        next: (res) => {
          this.router.navigate(['/']);
          this.snackbarService.closeAllSnackbar()
          this.authService.saveUserDetails(res);
          // const webUser = res.success.results.user_details;
          // localStorage.setItem('web_user', JSON.stringify(webUser));
          // localStorage.setItem('settings', JSON.stringify(res.success.results.settings));
          // localStorage.setItem('customer_details', JSON.stringify(res.success.results.customer_details));
          // localStorage.setItem('role_code', res.success.results.role_code);
        },
        error: () => {
          this.errorMessage = 'Authentication error.';
          this.loading = false;
        }
    });
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  onWarehouseSelect(warehouse: any): void {
    // localStorage.setItem('warehouse_code', warehouse['code']);
    this.loginService.selectedWarehouse = warehouse['code'];
    this.getUserDetail(warehouse['database_schema']);
  }
}

const BASE_ELEMENT_ID: string = 'id_login';
const ELEMENT_IDS: { [key: string]: string } = {
  main: `${BASE_ELEMENT_ID}_main`,
  left: `${BASE_ELEMENT_ID}_left`,
  slider: `${BASE_ELEMENT_ID}_slider`,
  right: `${BASE_ELEMENT_ID}_right`,
  content: `${BASE_ELEMENT_ID}_content`,

  hdr: `${BASE_ELEMENT_ID}_hdr`,
  sec_logo: `${BASE_ELEMENT_ID}_sec_logo`,
  img_logo: `${BASE_ELEMENT_ID}_img_logo`,
  lbl_title: `${BASE_ELEMENT_ID}_lbl_title`,
  lbl_subtitle: `${BASE_ELEMENT_ID}_lbl_subtitle`,

  form: `${BASE_ELEMENT_ID}_form`,
  input_username: `${BASE_ELEMENT_ID}_input_username`,
  input_password: `${BASE_ELEMENT_ID}_input_password`,
  btn_toggle_password: `${BASE_ELEMENT_ID}_btn_toggle_password`,
  lnk_forgot_password: `${BASE_ELEMENT_ID}_lnk_forgot_password`,
  msg_error: `${BASE_ELEMENT_ID}_msg_error`,
  btn_sign_in: `${BASE_ELEMENT_ID}_btn_sign_in`,

  sec_select_warehouse: `${BASE_ELEMENT_ID}_sec_select_warehouse`,
  sec_forgot_password: `${BASE_ELEMENT_ID}_sec_forgot_password`,
  sec_update_password: `${BASE_ELEMENT_ID}_sec_update_password`,

  ftr: `${BASE_ELEMENT_ID}_ftr`,
  lbl_copyright: `${BASE_ELEMENT_ID}_lbl_copyright`,
  lbl_version: `${BASE_ELEMENT_ID}_lbl_version`,
  lnk_privacy_policy: `${BASE_ELEMENT_ID}_lnk_privacy_policy`,
};
